import React, { useMemo, useState, useRef, useEffect } from 'react';
import Select, { components } from 'react-select';
import { useIsRequiredElement } from '../../../hook/useIsRequiredElement';
import ElementErrorMessage from './components/element-error-message';
import cx from 'classnames';
import { GridYValue, GridYUnit } from '../../../constants';
import { isMobile, isTablet } from 'react-device-detect';

export const DropdownElement = function FromElement({ element, handlerChange, error, formHasOnlyOneSubmitter, scale, previewTabItem }) {
  const selectRef = useRef();
  const isRequiredElement = useIsRequiredElement(element, formHasOnlyOneSubmitter);
  // With the label on the left it belongs to a fixed column the constructor
  // also draws, so the width comes from the stylesheet. Above the input it
  // still hugs its own text.
  const labelPlacementLeft = element.options.labelPlacement === 'left';
  const [otherInputValue, setOtherInputValue] = useState('');

  const options = useMemo(() => {
    const optionsList = element.inputs.map((item) => ({
      label: item.label,
      value: item.id,
    }));

    if (element.options.addOtherOption) {
      optionsList.push({ value: 'other', label: 'Other' });
    }

    return optionsList;
  }, [element.inputs]);

  const shouldUseOtherInput = () => {
    return element.options.addOtherOption && element.options.valueInput?.label === 'Other';
  };

  useEffect(() => {
    if (shouldUseOtherInput()) {
      setOtherInputValue(element.options.otherInputValue);
    }
  }, [element.options.addOtherOption]);

  const getInputValue = () => {
    if (shouldUseOtherInput()) {
      return otherInputValue;
    }

    return element.options.valueInput?.label || '';
  };

  const handleInputChange = (value) => {
    if (shouldUseOtherInput() && (value || (otherInputValue.length === 1 && value.length === 0))) {
      setOtherInputValue(value);
      handlerChange(element, { value: 'other', label: 'Other', otherInputValue: value });
    }
  };

  const SingleValue = ({ children, ...props }) => <components.SingleValue {...props} />;

  const handleChangeValue = (e) => {
    if (e.label !== 'Other') {
      resetOtherInputValue();
    } else {
      setTimeout(() => {
        if (selectRef.current) {
          selectRef.current.focus();
        }
      }, 0);
    }
    handlerChange(element, e);
  };

  const resetOtherInputValue = () => {
    setOtherInputValue('');
    selectRef.current.blur();
  };

  return (
    <div
      className={cx('form-element-flex-label-wrapper', {
        labelPlacementLeft,
        isMobile: isMobile || previewTabItem === 'phone',
        isTablet: isTablet || previewTabItem === 'tablet',
      })}
    >
      <label
        htmlFor={element.id}
        className="form-label"
        style={{
          zIndex: 5000 + (element.options.zIndex || 10),
          ...(labelPlacementLeft ? {} : { width: 'fit-content' }),
          position: 'relative',
        }}
      >
        {element.options.label} <span className="required">{isRequiredElement ? '*' : ''}</span>
      </label>
      <Select
        ref={selectRef}
        id={element.id}
        options={options}
        menuPosition={'fixed'}
        inputValue={getInputValue()}
        classNames={{ input: () => 'dropdown-input' }}
        components={{ SingleValue }}
        name={element.options.name}
        classNamePrefix="react-select"
        menuPortalTarget={document.body}
        readOnly={element.options.readOnly}
        value={element.options.valueInput}
        filterOption={null}
        styles={{
          menu: (provided) => ({
            ...provided,
            minWidth: 'unset',
            maxWidth: (1100 * element.options.width) / 100 + 'px',
            width: (1100 * element.options.width) / 100 + 'px',
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }),
          input: (provided) => ({
            ...provided,
            input: {
              opacity: '1 !important',
            },
          }),
          menuList: (provided) => ({
            ...provided,
            minHeight: options.length > 3 ? 168 : undefined,
            maxHeight: 248,
          }),
          menuPortal: (base) => ({ ...base, zIndex: 999999 }),
          control: (styles) => ({
            ...styles,
            height: element.options.innerElemHeight * GridYValue + GridYUnit,
            minHeight: 'unset !important',
            width: '100% !important',
            zIndex: 5000 + (element.options.zIndex || 10),
            position: 'relative',
          }),
        }}
        // The field's width is already set by the grid (`options.width` on the wrapper), the way
        // the constructor canvas renders it. The legacy small/medium/large `size` classes would
        // shrink the control to 50%/75% of that on top of it, so the dropdown came out narrower
        // here (preview / initialization / view / print) than it looks while being placed.
        className="form-control-select"
        onChange={(e, meta) => handleChangeValue(e, meta)}
        onInputChange={handleInputChange}
      />
      <ElementErrorMessage message={error} />
      {element.options.instructions && <p className="instructions">{element.options.instructions}</p>}
    </div>
  );
};
